import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Turnstile } from '@/components/ui/Turnstile';

interface FormData {
  fullName: string;
  email: string;
  workingOn: string;
  consent: boolean;
}

export function IndividualIntroForm() {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    workingOn: '',
    consent: false,
  });

  const [turnstileToken, setTurnstileToken] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }

    if (!formData.email.trim() || !formData.email.includes('@') || !formData.email.includes('.')) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!formData.workingOn.trim()) {
      setError('Please tell us what you are working on right now.');
      return;
    }

    if (!formData.consent) {
      setError('Please consent to the privacy policy to submit your request.');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the Turnstile verification.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'debrief-request',
          fullName: formData.fullName,
          email: formData.email,
          workingOn: formData.workingOn,
          consent: formData.consent,
          turnstileToken,
          company_website: companyWebsite,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to submit your request. Please try again.');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong submitting your request. Please try again or email office@enneainternational.com.');
      setTurnstileToken('');
      setResetKey(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div 
        id="intro-request-success" 
        className="bg-white border border-ground-deep p-8 md:p-10 shadow-sm text-center"
      >
        <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif text-ink mb-3">Thank you, {formData.fullName}</h3>
        <p className="text-ink/80 text-sm leading-relaxed mb-6">
          Your debrief request has been received. We will contact you at <span className="font-semibold text-ink">{formData.email}</span> within two working days to connect you with an accredited FIVE LENS practitioner.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ground-deep p-8 md:p-10 shadow-sm">
      <h3 className="text-2xl font-serif text-ink mb-2">Request a FIVE LENS Debrief</h3>
      <p className="text-sm text-ink/70 mb-8">
        Connect with an accredited practitioner for your 1-on-1 debrief session.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm flex items-start gap-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot */}
        <input
          type="text"
          name="company_website"
          value={companyWebsite}
          onChange={(e) => setCompanyWebsite(e.target.value)}
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />

        <div>
          <label htmlFor="intro-fullName" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
            Full Name <span className="text-rose-600">*</span>
          </label>
          <input
            id="intro-fullName"
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            disabled={loading}
            required
            placeholder="Alex Smith"
            className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50"
          />
        </div>

        <div>
          <label htmlFor="intro-email" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
            Email Address <span className="text-rose-600">*</span>
          </label>
          <input
            id="intro-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            required
            placeholder="alex@example.com"
            className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50"
          />
        </div>

        <div>
          <label htmlFor="intro-workingOn" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
            What are you working on right now? <span className="text-rose-600">*</span>
          </label>
          <textarea
            id="intro-workingOn"
            name="workingOn"
            rows={4}
            value={formData.workingOn}
            onChange={handleChange}
            disabled={loading}
            required
            placeholder="Describe any challenges, transitions, leadership hurdles, or personal growth areas you are focusing on..."
            className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50 resize-y"
          />
        </div>

        <div className="pt-2">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              id="intro-consent"
              type="checkbox"
              name="consent"
              checked={formData.consent}
              onChange={handleChange}
              disabled={loading}
              required
              className="w-4 h-4 mt-1 accent-rule-3 cursor-pointer border-ground-deep rounded"
            />
            <span className="text-xs text-ink/80 leading-relaxed">
              I agree to the{' '}
              <Link to="/privacy-policy" target="_blank" className="underline hover:text-rule-3 font-medium">
                Privacy Policy
              </Link>{' '}
              and consent to being contacted regarding my debrief and introduction request. <span className="text-rose-600">*</span>
            </span>
          </label>
        </div>

        <Turnstile 
          onVerify={(token) => { setTurnstileToken(token); setError(null); }}
          onExpire={() => { setTurnstileToken(''); setError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
          onError={() => { setTurnstileToken(''); setError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
          resetKey={resetKey} 
        />

        <div>
          <button
            id="intro-submit-btn"
            type="submit"
            disabled={loading || !turnstileToken}
            className="w-full sm:w-auto bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/70 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Submitting Debrief Request...</span>
              </>
            ) : (!turnstileToken ? (
              <span>Verifying…</span>
            ) : (
              'Request FIVE LENS Debrief'
            ))}
          </button>
        </div>
      </form>

      <p className="text-xs text-ink/60 mt-6 pt-6 border-t border-ground-deep leading-relaxed">
        Debriefs are conducted by accredited FIVE LENS coaches. We will contact you within two working days to connect you with a practitioner suited to what you are working on.
      </p>
    </div>
  );
}
